import React from 'react';

import { Flex, Text } from '@opendoor/bricks/core';

import { formatCurrency } from 'shared/helpers/formatters';
import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';

import { calculateClosingCosts } from 'buying/components/purchaseCalculator/utils/PurchaseCalculatorUtils';

interface ICostBreakdownProps {
  buyDirectDiscount: number;
  headline?: string;
  downPaymentAmount?: number;
  downPaymentPercentage?: number;
  listPrice: number;
}

/**
 * Cost breakdown table for the Buy Direct transaction
 * storybook-check-ignore
 */
export const CostBreakdown: React.FC<ICostBreakdownProps> = React.memo(
  ({ buyDirectDiscount, downPaymentAmount, downPaymentPercentage, headline, listPrice }) => {
    const { isMobile } = useMobileDevice();
    const buyDirectPrice = listPrice * buyDirectDiscount;
    const buyDirectClosingCosts = calculateClosingCosts(buyDirectPrice);
    const buyDirectTotal = buyDirectClosingCosts + buyDirectPrice;
    return (
      <Flex flexDirection="column" width="100%" alignItems="flex-start" mt={8} mb={4}>
        <Text size={isMobile ? '160' : '150'} fontWeight="medium">
          {headline ?? 'What you’ll pay'}
        </Text>
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          width="100%"
          justifyContent="space-between"
          mt={8}
        >
          <Flex flexDirection="row" width="100%" justifyContent="space-between" mb={7}>
            <Text size={isMobile ? '100' : '110'}>Buy from Opendoor buy direct price</Text>
            <Text size={isMobile ? '100' : '110'}>{formatCurrency(buyDirectPrice)}</Text>
          </Flex>
          <Flex flexDirection="row" width="100%" justifyContent="space-between" mb={7}>
            <Text size={isMobile ? '100' : '110'}>Agent commissions</Text>
            <Text size={isMobile ? '100' : '110'}>{formatCurrency(0)}</Text>
          </Flex>
          <Flex flexDirection="row" width="100%" justifyContent="space-between" mb={7}>
            <Text size={isMobile ? '100' : '110'}>Estimated closing costs</Text>
            <Text size={isMobile ? '100' : '110'}>{formatCurrency(buyDirectClosingCosts)}</Text>
          </Flex>
          <Flex flexDirection="row" width="100%" justifyContent="space-between" mb={7}>
            <Text fontWeight="medium" size={isMobile ? '100' : '110'}>
              Total amount with closing costs
            </Text>
            <Text fontWeight="medium" size={isMobile ? '100' : '110'}>
              {formatCurrency(buyDirectTotal)}
            </Text>
          </Flex>
          {downPaymentAmount && downPaymentPercentage && (
            <>
              <Flex flexDirection="row" width="100%" justifyContent="space-between" mt={4} mb={4}>
                <Text fontWeight="medium" size={isMobile ? '100' : '110'}>
                  Pay when you close
                </Text>
                <Text fontWeight="medium" size={isMobile ? '100' : '110'}>
                  {formatCurrency(downPaymentAmount + buyDirectClosingCosts)}
                </Text>
              </Flex>
              <Flex flexDirection="row" width="100%" justifyContent="space-between" mb={4}>
                <Text size={isMobile ? '90' : '100'}>
                  Down payment {Math.round(downPaymentPercentage)}%
                </Text>
                <Text size={isMobile ? '90' : '100'}>{formatCurrency(downPaymentAmount)}</Text>
              </Flex>
              <Flex flexDirection="row" width="100%" justifyContent="space-between" mb={4}>
                <Text size={isMobile ? '90' : '100'}>Closing costs (estimate)</Text>
                <Text size={isMobile ? '90' : '100'}>{formatCurrency(buyDirectClosingCosts)}</Text>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    );
  },
);

CostBreakdown.displayName = 'CostBreakdown';
